import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Card,
  CardContent,
  Grid,
  Stack
} from '@mui/material';
import { ArrowBack, OpenInNew, Download, BookmarkBorder, Bookmark } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import normalizeImageUrl from '../utils/normalizeImageUrl';
import { useAuth } from '../contexts/AuthContext';
import Spinner from '../components/Spinner';
import { readSchoolBookmarks, toggleSchoolBookmark } from '../utils/schoolBookmarks';

interface MaterialItem {
  _id: string;
  title: string;
  description: string;
  subject: { _id: string; name: string };
  className: string;
  fileType: string;
  fileUrl: string;
  fileName: string;
  isVisible: boolean;
  staff: { name: string };
  createdAt: string;
}

const StudentMaterials: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [materials, setMaterials] = useState<MaterialItem[]>([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(null);
  const [bookmarkedIds, setBookmarkedIds] = useState<Record<string, boolean>>({});

  const refreshBookmarks = async () => {
    const saved = await readSchoolBookmarks();
    const next: Record<string, boolean> = {};
    saved.forEach((bookmark) => {
      next[`${bookmark.type}:${bookmark.id}`] = true;
    });
    setBookmarkedIds(next);
  };

  const getMaterialSubjectGroupKey = (material: MaterialItem) => {
    const name = material.subject?.name?.trim();
    return name ? name.toLowerCase() : 'general';
  };

  const getMaterialSubjectGroupLabel = (material: MaterialItem) => {
    const name = material.subject?.name?.trim();
    return name || 'General/Uncategorized';
  };

  const isHighSchoolEducation = (educationLevel?: string) =>
    typeof educationLevel === 'string' && /high[- ]school/i.test(educationLevel);

  const hasSchoolWorkAccess = Boolean(
    isHighSchoolEducation(user?.educationLevel) &&
    (user?.school || user?.schoolName || user?.schoolEmail)
  );

  useEffect(() => {
    const fetchMaterials = async () => {
      setLoading(true);
      setError(null);
      try {
        const materialsRes = await api.get('/school-work/materials');
        setMaterials(Array.isArray(materialsRes.data) ? materialsRes.data : []);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Unable to load materials');
      } finally {
        setLoading(false);
      }
    };
    
    if (hasSchoolWorkAccess) {
      fetchMaterials();
      refreshBookmarks().catch(() => setError('Unable to load saved bookmarks'));
    } else {
      setLoading(false);
    }
  }, [hasSchoolWorkAccess, user?._id]);

  const handleToggleBookmark = async (material: MaterialItem) => {
    try {
      const isBookmarked = await toggleSchoolBookmark({
      id: material._id,
      type: 'material',
      title: material.title,
      subject: material.subject?.name || 'General',
      route: '/school-work/materials',
      details: `${material.fileType || 'File'} • ${material.className || 'All classes'}`,
      savedAt: new Date().toISOString(),
      });
      setBookmarkedIds((current) => ({ ...current, [`material:${material._id}`]: isBookmarked }));
    } catch {
      setError('Unable to update saved material. Please sign in and try again.');
    }
  };

  if (!hasSchoolWorkAccess && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          School Work is available only to high school students registered under a registered school.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Materials</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/school-work')}>
          Back to Dashboard
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      ) : (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>Materials uploaded by your teachers</Typography>
          {materials.length === 0 ? (
            <Alert severity="info">No materials available for your class yet.</Alert>
          ) : !selectedSubjectId ? (
            <Grid container spacing={3}>
              {Object.values(
                materials.reduce((acc, material) => {
                  const subjectKey = getMaterialSubjectGroupKey(material);
                  const subjectName = getMaterialSubjectGroupLabel(material);
                  if (!acc[subjectKey]) {
                    acc[subjectKey] = { _id: subjectKey, name: subjectName, count: 0 };
                  }
                  acc[subjectKey].count += 1;
                  return acc;
                }, {} as Record<string, { _id: string; name: string; count: number }>)
              ).map((subject: any) => (
                <Grid item xs={12} sm={6} md={4} key={subject._id}>
                  <Card 
                    sx={{ cursor: 'pointer', transition: '0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}
                    onClick={() => setSelectedSubjectId(subject._id)}
                  >
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 1 }}>{subject.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {subject.count} Material{subject.count !== 1 ? 's' : ''}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box>
              <Button sx={{ mb: 2 }} onClick={() => setSelectedSubjectId(null)} startIcon={<ArrowBack />}>
                Back to Subjects
              </Button>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Class</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Teacher</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {materials
                      .filter(m => getMaterialSubjectGroupKey(m) === selectedSubjectId)
                      .map((material) => (
                      <TableRow key={material._id}>
                        <TableCell>{material.title}</TableCell>
                        <TableCell>{material.subject?.name || 'General'}</TableCell>
                        <TableCell>{material.className || 'All'}</TableCell>
                        <TableCell>{material.fileType || 'file'}</TableCell>
                        <TableCell>{material.staff?.name || 'Teacher'}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant={bookmarkedIds[`material:${material._id}`] ? 'contained' : 'outlined'}
                              color="warning"
                              startIcon={bookmarkedIds[`material:${material._id}`] ? <Bookmark /> : <BookmarkBorder />}
                              onClick={() => handleToggleBookmark(material)}
                            >
                              {bookmarkedIds[`material:${material._id}`] ? 'Saved' : 'Save'}
                            </Button>
                            {material.fileUrl ? (
                              <>
                                <Button
                                  component="a"
                                  href={normalizeImageUrl(material.fileUrl) || material.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  size="small"
                                  variant="outlined"
                                  startIcon={<OpenInNew />}
                                >
                                  Open
                                </Button>
                                <Button
                                  component="a"
                                  href={normalizeImageUrl(material.fileUrl) || material.fileUrl}
                                  download={material.fileName || true}
                                  size="small"
                                  variant="contained"
                                  startIcon={<Download />}
                                >
                                  Download
                                </Button>
                              </>
                            ) : (
                              <Button size="small" disabled>
                                No file
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StudentMaterials;
